import React, { createContext, useContext, useState, useEffect } from 'react';

interface User {
  id: string;
  email: string;
  full_name: string;
  role: 'student' | 'admin' | 'counselor';
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkDemoSession = () => {
      try {
        const demoUser = localStorage.getItem('demo-admin-user');
        if (demoUser) {
          const parsedUser = JSON.parse(demoUser);
          setUser(parsedUser);
        }
      } catch (error) {
        console.error('Error parsing demo user:', error);
        localStorage.removeItem('demo-admin-user');
      }
      setLoading(false);
    };

    checkDemoSession();
  }, []);

  const signIn = async (email: string, password: string) => {
    const now = new Date().toISOString();

    // Demo credentials
    const demoUsers: { [email: string]: User & { password: string } } = {
      'admin@gradvikalp.com': {
        id: 'demo-admin-id',
        email: 'admin@gradvikalp.com',
        full_name: 'Admin User',
        role: 'admin',
        created_at: now,
        updated_at: now,
        password: 'GradVikalp@54321',
      },
      'student@gradvikalp.com': {
        id: 'demo-student-id',
        email: 'student@gradvikalp.com',
        full_name: 'Student User',
        role: 'student',
        created_at: now,
        updated_at: now,
        password: 'Student123',
      },
      'intern@gradvikalp.com': {
        id: 'demo-counselor-id',
        email: 'intern@gradvikalp.com',
        full_name: 'Counselor User',
        role: 'counselor',
        created_at: now,
        updated_at: now,
        password: 'intern@grad',
      },
    };

    const demoUser = demoUsers[email];
    if (demoUser && demoUser.password === password) {
      const { password: _, ...userWithoutPassword } = demoUser;
      setUser(userWithoutPassword);
      localStorage.setItem('demo-admin-user', JSON.stringify(userWithoutPassword));
      return;
    }

    throw new Error('Invalid credentials');
  };

  const signOut = async () => {
    setUser(null);
    localStorage.removeItem('demo-admin-user');
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'counselor';
  const isAdminOnly = user?.role === 'admin';

  const value = {
    user,
    loading,
    signIn,
    signOut,
    isAdmin,
    isAdminOnly,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
